import type { ICrossChatSearchHit } from "@frontend-shared/types";
import { getDateFormat } from "@frontend-shared/utils/date";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { Message } from "@/components/icons";
import { SpinningLoader } from "@/components/SpinningLoader";
import { Button } from "@/components/ui/button";
import { CommandGroup, CommandItem } from "@/components/ui/command";
import { getAutoDirection } from "@/lib/direction";

import {
  GLOBAL_SEARCH_ROW_CLASS_NAME,
  GLOBAL_SEARCH_ROW_ICON_CLASS_NAME,
} from "./global-search/constants";

interface ISemanticSearchResultsProps {
  isLoading: boolean;
  isError: boolean;
  hits: ICrossChatSearchHit[];
  onRetry: () => void;
  onSelectHit: (hit: ICrossChatSearchHit) => void;
}

export const SemanticSearchResults = ({
  isLoading,
  isError,
  hits,
  onRetry,
  onSelectHit,
}: ISemanticSearchResultsProps) => {
  const { t, i18n } = useTranslation("chat");

  if (isLoading) {
    return (
      <div
        className="h-full flex items-center justify-center"
        data-testid="semantic-search-loading"
      >
        <SpinningLoader />
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="h-full flex items-center justify-center flex-col gap-4"
        data-testid="semantic-search-error"
      >
        <span className="text-sm text-foreground-secondary">
          {t("search.error_loading")}
        </span>
        <Button data-testid="semantic-search-retry" onClick={onRetry}>
          {t("search.retry")}
        </Button>
      </div>
    );
  }

  // NOTE: The 0-hit case is intentionally unrendered — `GlobalSearchChatsSection` only
  // mounts this component when `isLoading || isError || hits.length > 0`,
  // so an empty-state branch here would be unreachable. The title-section
  // empty state in `SearchResults` covers the "nothing matched anywhere"
  // case for the whole dialog.

  return (
    <CommandGroup className="p-0" data-testid="semantic-search-items-group">
      {hits.map((hit) => (
        <CommandItem
          // NOTE: messageUuid is unique per result row and stable across
          // re-renders. Using it as the key avoids cmdk re-mounting the
          // same row when the query updates incrementally.
          key={hit.messageUuid}
          value={hit.messageUuid}
          onSelect={() => onSelectHit(hit)}
          className={GLOBAL_SEARCH_ROW_CLASS_NAME}
          data-testid={`semantic-search-item-${hit.messageUuid}`}
        >
          <div className="flex min-w-0 grow items-start gap-3">
            <span className={GLOBAL_SEARCH_ROW_ICON_CLASS_NAME}>
              <Message className="h-[18px] w-[18px]" />
            </span>
            <div className="flex min-w-0 flex-col">
              <p
                dir={getAutoDirection({ content: hit.chatTitle ?? undefined })}
                className="truncate text-start text-sm text-foreground"
              >
                {hit.chatTitle ?? t("search.semantic_untitled_chat")}
              </p>
              <p
                dir={getAutoDirection({ content: hit.snippet })}
                className="truncate text-start text-xs text-foreground-secondary"
              >
                {hit.snippet}
              </p>
              <p className="text-[11px] text-foreground-secondary/80">
                {/* NOTE: Project label is omitted entirely when projectName
                    is null (concurrent project soft-delete on the server)
                    so the row doesn't get a dangling "Project: " prefix. */}
                {hit.projectName !== null && (
                  <>
                    {t("search.semantic_project_label", {
                      project: hit.projectName,
                    })}
                    <span className="px-1">·</span>
                  </>
                )}
                {format(new Date(hit.matchedAt), getDateFormat(i18n.language))}
              </p>
            </div>
          </div>
        </CommandItem>
      ))}
    </CommandGroup>
  );
};
